import { Button, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { FixtureAction } from '@/service/types';
import { useCommentAction } from '@/components/ActionsDialog/useCommentAction';
import {
  COMMON_STRING,
  DELETE_CONFIRMATION_STRING,
} from '@/constants/dictionary';
import { API_METHOD } from '@/service/constants';
import { ConfirmationButtonSet } from '../common/ConfirmationButtonSet';
import { TEXT_FIELD_CONSTANCE } from '../ActionEvaluationDialog/constants';

interface CommentButtonsProps {
  action: FixtureAction;
  onClose: VoidFunction;
  comment: FixtureAction['comment'];
  hasLocalChanges: boolean;
  hasRemoteChanges: boolean;
  onSyncClick: () => void;
}
export const getSyncTooltipTitle = (comment: FixtureAction['comment']) => {
  return (
    <>
      <Typography variant='subtitle2'>This comment has changed.</Typography>
      <Typography variant='subtitle2'>New comment:</Typography>
      <Typography variant='subtitle1'>{comment}</Typography>
    </>
  );
};

export const CommentButtons = ({
  action,
  onClose,
  comment,
  hasLocalChanges,
  hasRemoteChanges,
  onSyncClick,
}: CommentButtonsProps) => {
  const { commentAction } = useCommentAction();
  const theme = useTheme();
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] =
    useState(false);

  const dataToSend = {
    comment,
    collectionId: action.collectionId,
    fixtureId: action.fixtureId,
    fixtureActionId: action.id,
  };

  const handleCloseDeleteConfirmation = () =>
    setIsOpenDeleteConfirmation(false);
  const handleRemoveComment = () => {
    commentAction({
      commentActionType: API_METHOD.DELETE,
      comment: dataToSend,
    });
    onClose();
  };

  return (
    <Stack flexDirection='row' gap={1} justifyContent='end'>
      <Tooltip
        open={isOpenDeleteConfirmation}
        disableHoverListener
        title={
          <ConfirmationButtonSet
            onConfirm={handleRemoveComment}
            onClose={handleCloseDeleteConfirmation}
            confirmMessage={DELETE_CONFIRMATION_STRING.COMMENT}
          />
        }
        placement='left'
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[1],
            },
          },
        }}
      >
        <Button
          variant={'text'}
          disabled={isOpenDeleteConfirmation}
          onClick={() => {
            comment && action.comment
              ? setIsOpenDeleteConfirmation(true)
              : onClose();
          }}
        >
          {comment && action.comment
            ? COMMON_STRING.REMOVE
            : COMMON_STRING.CLOSE}
        </Button>
      </Tooltip>
      <Button
        variant={hasRemoteChanges ? 'outlined' : 'contained'}
        disabled={
          !hasLocalChanges ||
          !comment ||
          comment.length > TEXT_FIELD_CONSTANCE.CHARACTERS_LIMIT
        }
        onClick={() => {
          !action.comment || !action.comment.length
            ? commentAction({
                commentActionType: API_METHOD.POST,
                comment: dataToSend,
              })
            : commentAction({
                commentActionType: API_METHOD.PUT,
                comment: dataToSend,
              });
          onClose();
        }}
      >
        {hasRemoteChanges
          ? COMMON_STRING.OVERWRITE
          : !action.comment || !action.comment.length
          ? COMMON_STRING.ADD
          : COMMON_STRING.UPDATE}
      </Button>
      {hasRemoteChanges && (
        <Tooltip
          sx={{ maxWidth: 500 }}
          placement='top'
          title={getSyncTooltipTitle(action.comment)}
        >
          <Button variant='contained' onClick={onSyncClick}>
            {COMMON_STRING.SYNC}
          </Button>
        </Tooltip>
      )}
    </Stack>
  );
};
